import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO, breadcrumb } from "@/lib/seo";
import { useI18n } from "@/i18n";
import { FAQ } from "@/components/FAQ";
import DevisForm from "@/components/DevisForm";
import { SectionTitle, DataTable, Breadcrumbs } from "@/components/PageBlocks";
import { trackPhoneClick } from "@/lib/analytics";
import { ArrowRight, Check, Phone } from "lucide-react";
import { MARIAGE_CONTENT } from "@/content/mariage";
import { usePageContent } from "@/lib/pageContent";
import { pageImageUrl } from "@/content/pageImages";
import { Rich } from "@/components/Rich";


const SITE = "https://lentracterestaurant.fr";

const WEDDING_SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE}/mariage#service`,
  "name": "Réception de mariage à L'Entracte",
  "serviceType": "Réception de mariage",
  "description":
    "Mariage clé en main dans un hôtel particulier à Ville-d'Avray : vin d'honneur, repas du chef, vin, eaux et café, de 12 à 100 invités assis, terrasse de 240 m² sur parc.",
  "provider": { "@id": `${SITE}/#lieu` },
  "areaServed": [
    { "@type": "City", "name": "Ville-d'Avray" },
    { "@type": "City", "name": "Versailles" },
    { "@type": "City", "name": "Sèvres" },
    { "@type": "City", "name": "Saint-Cloud" },
    { "@type": "City", "name": "Chaville" },
    { "@type": "City", "name": "Boulogne-Billancourt" },
    { "@type": "City", "name": "Paris" },
  ],
  "offers": {
    "@type": "Offer",
    "url": `${SITE}/mariage`,
    "priceCurrency": "EUR",
    "priceSpecification": {
      "@type": "UnitPriceSpecification",
      "priceCurrency": "EUR",
      "minPrice": 99,
      "maxPrice": 125,
      "valueAddedTaxIncluded": true,
      "unitText": "par invité",
    },
  },
};

export default function Mariage() {
  const { lang, localePath, alternateUrls } = useI18n();
  const c = usePageContent("mariage", lang === "en" ? "en" : "fr", MARIAGE_CONTENT[lang === "en" ? "en" : "fr"]);
  const [imgs, setImgs] = useState({});

  useSEO({
    title: c.meta.title,
    description: c.meta.description,
    keywords: c.meta.keywords,
    lang,
    alternates: alternateUrls(),
    jsonLd: [
      breadcrumb([
        { name: lang === "en" ? "Home" : "Accueil", url: `${SITE}/` },
        { name: lang === "en" ? "Events" : "Événements", url: `${SITE}/evenements` },
        { name: lang === "en" ? "Wedding" : "Mariage", url: `${SITE}/mariage` },
      ]),
      WEDDING_SERVICE_LD,
    ],
  });

  useEffect(() => {
    api.get("/settings/public").then((r) => setImgs(r.data || {})).catch(() => {});
  }, []);

  const img = (slot) => pageImageUrl(imgs, "mariage", slot);
  const HERO = img("hero");
  const SALON = img("salon");
  const COCKTAIL = img("cocktail");
  const DESSERT = img("dessert");
  const DECOR = img("decor");
  const SPACE_IMGS = [DECOR, SALON, DESSERT, COCKTAIL];

  const crumbs = c.breadcrumb.map((b) => ({
    label: b.label,
    to: b.to ? localePath(b.to) : undefined,
  }));

  return (
    <div data-testid="mariage-page">
      {/* HERO */}
      <section className="relative min-h-[62vh] flex items-end overflow-hidden -mt-[148px] pt-[148px]">
        {HERO && <img src={HERO} alt={c.heroAlt} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/65" />
        <div className="relative z-10 max-w-[1500px] mx-auto w-full px-6 md:px-12 lg:px-16 py-16 md:py-24">
          <p className="overline text-[#E6CFA1]">{c.overline}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] text-white font-light max-w-4xl">
            {c.h1}
          </h1>
          <Rich as="p" value={c.intro} className="mt-8 text-white/85 text-base md:text-lg max-w-3xl leading-relaxed" />
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#devis" data-testid="hero-cta-devis" className="btn-gold">
              {c.ctaDevis} <ArrowRight size={14} />
            </a>
            <a
              href="tel:+33182001867"
              data-testid="hero-cta-phone"
              onClick={() => trackPhoneClick("mariage_hero")}
              className="btn-ghost-light"
            >
              <Phone size={14} /> {c.ctaCall}
            </a>
          </div>
        </div>
      </section>

      {/* FIL D'ARIANE */}
      <div className="bg-[#F6F0E7] border-b border-[#D9D3C5]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-4">
          <Breadcrumbs items={crumbs} testid="mariage-breadcrumbs" />
        </div>
      </div>

      {/* EN BREF */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1100px] mx-auto">
          <SectionTitle>{c.brief.title}</SectionTitle>
          <div className="mt-10">
            <DataTable caption={c.brief.caption} rows={c.brief.rows} testid="mariage-brief-table" />
          </div>
        </div>
      </section>

      {/* MARIAGE À TAILLE HUMAINE */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-7">
            <SectionTitle>{c.human.title}</SectionTitle>
            <Rich as="p" value={c.human.p1} className="mt-8 text-[#3D4A5A] leading-relaxed max-w-2xl" />
            <Rich as="p" value={c.human.p2} className="mt-5 text-[#3D4A5A] leading-relaxed max-w-2xl" />
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              {SALON && <img src={SALON} alt={c.human.alt} className="w-full h-full object-cover img-soft" />}
            </div>
          </div>
        </div>
      </section>

      {/* POURQUOI */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.why.title}</SectionTitle>
          <ul className="mt-10 space-y-5">
            {c.why.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ESPACES */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1500px] mx-auto">
          <SectionTitle>{c.spaces.title}</SectionTitle>
          <div className="mt-12 space-y-16 md:space-y-24">
            {c.spaces.items.map((sp, i) => (
              <div key={sp.name} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    {SPACE_IMGS[i] && (
                      <img src={SPACE_IMGS[i]} alt={sp.alt} className="w-full h-full object-cover img-soft" />
                    )}
                  </div>
                </div>
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <h3 className="font-display text-2xl md:text-3xl text-[#203240] font-light">{sp.name}</h3>
                  <Rich as="p" value={sp.body} className="mt-5 text-[#3D4A5A] leading-relaxed max-w-xl" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-20 max-w-[900px]">
            <DataTable
              caption={c.spaces.table.caption}
              head={c.spaces.table.head}
              rows={c.spaces.table.rows}
              testid="mariage-capacity-table"
            />
            <p className="mt-8 text-[#697585] leading-relaxed">{c.spaces.note}</p>
          </div>
        </div>
      </section>

      {/* DÉROULÉ */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-7">
            <SectionTitle>{c.timeline.title}</SectionTitle>
            <ol className="mt-12 space-y-8" data-testid="mariage-timeline">
              {c.timeline.steps.map((s, i) => (
                <li key={s.label} className="flex items-start gap-6">
                  <span className="font-display text-3xl text-[#B8956A] leading-none w-10 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-[#3D4A5A] leading-relaxed pt-1">
                    <Rich value={s.label} className="text-[#203240]" /> <Rich value={s.body} />
                  </p>
                </li>
              ))}
            </ol>
            <p className="mt-10 italic text-[#697585] leading-relaxed">{c.timeline.note}</p>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              {COCKTAIL && (
                <img src={COCKTAIL} alt={c.timeline.alt} className="w-full h-full object-cover img-soft" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* MENU DE MARIAGE */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              {DESSERT && <img src={DESSERT} alt={c.menu.alt} className="w-full h-full object-cover img-soft" />}
            </div>
          </div>
          <div className="md:col-span-7">
            <SectionTitle>{c.menu.title}</SectionTitle>
            <Rich as="p" value={c.menu.intro} className="mt-8 text-[#3D4A5A] leading-relaxed" />

            <p className="mt-10 text-[#B8956A] uppercase tracking-[0.22em] text-xs">{c.menu.tableTitle}</p>
            <div className="mt-6">
              <DataTable
                caption={c.menu.tableCaption}
                rows={c.menu.tableRows}
                testid="mariage-menu-table"
              />
            </div>
            <Rich as="p" value={c.menu.choiceNote} className="mt-8 text-[#3D4A5A] leading-relaxed" />

            <p className="mt-12 text-[#B8956A] uppercase tracking-[0.22em] text-xs">{c.menu.groupsTitle}</p>
            <dl className="mt-6 space-y-8">
              {c.menu.groups.map((g) => (
                <div key={g.label}>
                  <dt className="text-xs uppercase tracking-[0.22em] text-[#697585]">{g.label}</dt>
                  <Rich as="dd" value={g.body} className="mt-3 text-[#3D4A5A] leading-relaxed" />
                </div>
              ))}
            </dl>
            <p className="mt-10 italic text-[#697585]">{c.menu.note}</p>
            <Link to={localePath("/menu")} className="btn-ghost mt-10 inline-flex">
              {c.menu.ctaMenu} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* BUDGET */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.budget.title}</SectionTitle>
          <ul className="mt-10 space-y-5">
            {c.budget.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
          <Rich as="p" value={c.budget.note} className="mt-10 text-[#697585] leading-relaxed" />
        </div>
      </section>

      {/* PRESTATAIRES */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-7">
            <SectionTitle>{c.suppliers.title}</SectionTitle>
            <ul className="mt-10 space-y-5">
              {c.suppliers.items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                  <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                  <Rich value={it} />
                </li>
              ))}
            </ul>
            <Link to={localePath("/galerie")} className="btn-ghost mt-10 inline-flex">
              {c.suppliers.ctaGallery} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              {DECOR && <img src={DECOR} alt={c.suppliers.alt} className="w-full h-full object-cover img-soft" />}
            </div>
          </div>
        </div>
      </section>

      {/* ACCÈS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.access.title}</SectionTitle>
          <Rich as="p" value={c.access.intro} className="mt-8 text-[#3D4A5A] leading-relaxed" />
          <ul className="mt-8 space-y-5">
            {c.access.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* AVIS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#203240]">
        <div className="max-w-[1500px] mx-auto">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#F6F0E7] font-light">
            {c.reviews.title}
          </h2>
          <p className="mt-6 text-[#E6CFA1] tracking-wide">{c.reviews.ratings}</p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
            {c.reviews.quotes.map((q) => (
              <blockquote key={q.author} className="border-l border-[#B8956A] pl-6">
                <p className="font-display text-xl md:text-2xl text-[#F6F0E7] font-light leading-snug">
                  « {q.quote} »
                </p>
                <footer className="mt-5 text-sm text-[#F6F0E7]/60">— {q.author}</footer>
              </blockquote>
            ))}
          </div>
          <a
            href={c.reviews.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mariage-reportage-link"
            className="mt-12 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-[#E6CFA1] hover:text-white transition-colors"
          >
            {c.reviews.link} <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.faqTitle}</SectionTitle>
          <div className="mt-10">
            <FAQ items={c.faq} testid="mariage-faq" schemaId="ld-faq-mariage" />
          </div>
        </div>
      </section>

      {/* DEVIS */}
      <section id="devis" className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF] scroll-mt-32">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="overline">{c.overline}</p>
            <SectionTitle>{c.devis.title}</SectionTitle>
            <Rich as="p" value={c.devis.body} className="mt-8 text-[#3D4A5A] leading-relaxed" />
            <p className="mt-6 text-[#697585]">{c.devis.contact}</p>
          </div>
          <div className="md:col-span-7">
            <div className="border border-[#D9D3C5] bg-[#F6F0E7] p-7 md:p-10">
              <DevisForm defaultEventType={c.eventType} pageSource="/mariage" />
            </div>
          </div>
        </div>
      </section>

      {/* LIENS INTERNES */}
      <section className="py-16 px-6 md:px-12 lg:px-16 bg-[#F6F0E7] border-t border-[#D9D3C5]">
        <div className="max-w-[1500px] mx-auto">
          <p className="overline">{c.related.title}</p>
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
            {c.related.links.map((l) => (
              <Link
                key={l.to}
                to={localePath(l.to)}
                data-testid={`mariage-related-${l.to.replace("/", "")}`}
                className="inline-flex items-center gap-2 text-[#203240] hover:text-[#B8956A] transition-colors"
              >
                {l.label} <ArrowRight size={14} className="text-[#B8956A]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
